import React, { useState, useEffect, useRef } from "react";
import { FaSearch, FaFilter, FaSync, FaCalendarAlt } from "react-icons/fa";
import DatePicker from "react-datepicker";
import { useToast } from "../context/ToastContext";
import { formatDate } from "../utils/date";
// import { useModal } from "../context/ConfirmModalContext";

interface DashboardHeaderProps {
  title: string;
  categories: string[];
  search?: string;
  setSearch?: (value: string) => void;
  onCategorySelect?: (category: string | null) => void;
  onAddNew?: () => void;
  onSync?: () => Promise<void> | void;
  lastSyncedAt?: Date | null;
  showFilter?: boolean;
  onClickSearch?: () => void;
  filterByDate?: boolean;
  onDateFilterApply?: (startDate: string, endDate: string) => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  title,
  categories,
  search,
  setSearch,
  onCategorySelect,
  onAddNew,
  onSync,
  lastSyncedAt,
  showFilter = true,
  onClickSearch,
  filterByDate = false,
  onDateFilterApply,
}) => {
  // const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCategoryLocal, setSelectedCategoryLocal] = useState<string | null>(null);
  const [loadingSync, setLoadingSync] = useState(false);
  const [showDatePopup, setShowDatePopup] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState<string | null>(null);

  const filterRef = useRef<HTMLDivElement | null>(null);
  const datePopupRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        filterRef.current &&
        !filterRef.current.contains(target)
      ) {
        setFilterOpen(false);
      }

      if (
        datePopupRef.current &&
        !datePopupRef.current.contains(target)
      ) {
        setShowDatePopup(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  const selectedCategory = selectedCategoryLocal;

  const handleCategorySelect = (category: string | null) => {
    setSelectedCategoryLocal(category);
    if (onCategorySelect) onCategorySelect(category);
    setFilterOpen(false);
  };

  const handleSync = async () => {
    if (!onSync) return;
    setLoadingSync(true);
    await onSync();
    setLoadingSync(false);
  };

  const handleApplyDateFilter = () => {
    if (startDate && endDate) {
      const displayRange = `${formatDate(startDate)} → ${formatDate(endDate)}`;
      setSelectedDateRange(displayRange);
      if (onDateFilterApply) onDateFilterApply(startDate, endDate);
      setShowDatePopup(false);
      setFilterOpen(false);
    }
  };

  const clearDateFilter = () => {
    setSelectedDateRange(null);
    setStartDate("");
    setEndDate("");
    if (onDateFilterApply) onDateFilterApply("", "");
  };

  return (<div className="mb-6">
    {/* Heading Row */}
    <div className="flex items-center justify-between mb-4">
      <h1 className="text-3xl font-bold text-gray-800">{title}</h1>

      <div className="flex items-center gap-3">
        {onSync && (
          <button
            onClick={handleSync}
            disabled={loadingSync}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg shadow hover:bg-blue-700 disabled:opacity-50"
          >
            <span className={loadingSync ? "animate-spin" : ""}>
              {FaSync({})}
            </span>
            {loadingSync ? "Syncing..." : `Sync ${title}`}
          </button>
        )}

        {onAddNew && (
          <button
            onClick={onAddNew}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700"
          >
            Add New {title}
          </button>
        )}
      </div>
    </div>

    {lastSyncedAt && (
      <p className="text-sm text-gray-500 mb-2">
        Last synchronized: {lastSyncedAt.toLocaleString()}
      </p>
    )}

    {/* Search & Filter Section */}
    <div className="flex items-center justify-between mb-4 space-x-4">
      <div className="relative flex-1 flex items-center">
        {setSearch && (
          <>
            <span
              className="absolute left-3 text-gray-400 cursor-pointer"
              onClick={onClickSearch}
            >
              {FaSearch({})}
            </span>
            <input
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded pl-10 pr-10 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </>
        )}

        {/* Selected Filter Display */}
        {(selectedCategory || selectedDateRange) && (
          <span
            className="absolute right-10 top-1/2 transform -translate-y-1/2 bg-blue-500 text-white text-sm px-2 py-1 rounded-full cursor-pointer"
            onClick={() => {
              if (selectedCategory) handleCategorySelect(null);
              if (selectedDateRange) clearDateFilter();
            }}
          >
            {selectedCategory ?? selectedDateRange} ✕
          </span>
        )}

        {/* Filter Button */}
        {showFilter && (
          <button
            onClick={() => setFilterOpen(!filterOpen)}
            className="absolute right-2 text-gray-600 p-1 hover:text-blue-500"
          >
            {FaFilter({})}
          </button>
        )}

        {/* Filter Dropdown */}
        {filterOpen && (
          <div
            ref={filterRef}
            className="absolute top-full right-0 mt-2 w-40 bg-white border border-gray-300 rounded shadow-lg z-50 max-h-64 overflow-y-auto"
          >
            {categories.map((category) => (
              <button
                key={category}
                className={`w-full text-left px-3 py-2 truncate hover:bg-blue-500 hover:text-white rounded ${selectedCategory === category ? "bg-blue-500 text-white" : ""
                  }`}
                onClick={() => handleCategorySelect(category)}
              >
                {category}
              </button>
            ))}

            {filterByDate && (
              <button
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-100 text-gray-700 rounded"
                onClick={() => {
                  setShowDatePopup(true);
                  setFilterOpen(false);
                }}
              >
                {FaCalendarAlt({})} Date
              </button>
            )}
          </div>
        )}

        {/* Date Filter Popup */}
        {showDatePopup && (
          <div
             ref={datePopupRef}
            className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-50"
          >
            <h3 className="font-semibold mb-2 text-gray-700">Select Date Range</h3>
            <div className="flex flex-col gap-2 mb-3">
              {/* Start Date */}
              {/* <div className="relative w-full">
    <DatePicker
      selected={startDate ? new Date(startDate) : null}
      onChange={(date: Date | null) => {
        if (!date) return;
        const newStart = date.toISOString().split("T")[0];
        if (endDate && newStart > endDate) {
          showToast("⚠️ Start date cannot be later than end date.", { type: "warning" });
          return;
        }
        setStartDate(newStart);
      }}
      placeholderText="Start Date"
      className="w-full border border-gray-300 rounded px-3 py-1 pr-10 focus:ring-2 focus:ring-blue-400 text-sm"
      dateFormat="MM/dd/yyyy"
    />
    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
      {FaCalendarAlt({})}
    </span>
  </div> */}
              <div className="w-full">
                <input
                  type="date"
                  value={startDate || ""}
                  onChange={(e) => {
                    const newStart = e.target.value;

                    if (endDate && newStart > endDate) {
                      showToast("⚠️ Start date cannot be later than end date.", { type: "warning" });
                      return;
                    }

                    setStartDate(newStart);
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-blue-400 text-sm"
                />
              </div>


              {/* End Date */}
              {/* <div className="relative w-full">
    <DatePicker
      selected={endDate ? new Date(endDate) : null}
      onChange={(date: Date | null) => {
        if (!date) return;
        const newEnd = date.toISOString().split("T")[0];
        if (!startDate) {
          return showToast("Please select a start date first.", { type: "info" });
        }
        if (newEnd < startDate) {
          showToast("End date cannot be earlier than start date.", { type: "warning" });
          return;
        }
        setEndDate(newEnd);
      }}
      placeholderText="End Date"
      className="w-full border border-gray-300 rounded px-3 py-1 pr-10 focus:ring-2 focus:ring-blue-400 text-sm"
      dateFormat="MM/dd/yyyy"
    />
    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
      {FaCalendarAlt({})}
    </span>
  </div> */}

              <div className="w-full">
                <input
                  type="date"
                  value={endDate || ""}
                  onChange={(e) => {
                    const newEnd = e.target.value;

                    if (!startDate) {
                      showToast("Please select a start date first.", { type: "info" });
                      return;
                    }

                    if (newEnd < startDate) {
                      showToast("End date cannot be earlier than start date.", { type: "warning" });
                      return;
                    }

                    setEndDate(newEnd);
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-blue-400 text-sm"
                />
              </div>

            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDatePopup(false)}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyDateFilter}
                disabled={!startDate || !endDate}
                className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notifications */}
      {/* <div className="flex items-center space-x-4">
      <div
        className="relative cursor-pointer"
        onClick={() =>
          openModal(
            "Coming Soon 🚀",
            "We’re still working on this feature. Stay tuned for the upcoming release.",
            closeModal,
            false,
            "Close"
          )
        }
      >
        <FaBell className="text-gray-600 text-xl" />
        <span className="absolute -top-1 -right-2 bg-red-500 text-white rounded-full text-xs px-1.5">
          3
        </span>
      </div>

      <div
        className="relative cursor-pointer"
        onClick={() =>
          openModal(
            "Coming Soon 🚀",
            "We’re still working on this feature. Stay tuned for the upcoming release.",
            closeModal,
            false,
            "Close"
          )
        }
      >
        <FaEnvelope className="text-gray-600 text-xl" />
        <span className="absolute -top-1 -right-2 bg-red-500 text-white rounded-full text-xs px-1.5">
          5
        </span>
      </div> */}
      {/* </div> */}
    </div>
  </div>
  );
};
